import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  getCategorias,
  createCategoria,
  updateCategoria,
} from "../../../services/admin/categorias_admin";
import CategoryFormModal from "./CategoryFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";

function CategoriesPage() {
  const [categorias, setCategorias] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // Cuantas filas por pagina; igual que en Products y Sales Orders,
  // se elige abajo junto a Prev/Next.
  const [pageSize, setPageSize] = useState(12);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);
  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const loadCategorias = useCallback(async () => {
    try {
      const array = await getCategorias();
      setCategorias(Array.isArray(array) ? array : []);
    } catch (error) {
      console.error(error);
      showAlert("error", error.message);
    }
  }, [showAlert]);

  useEffect(() => {
    loadCategorias();
  }, [loadCategorias]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categorias;
    return categorias.filter((c) =>
      String(c?.nombre || "").toLowerCase().includes(q)
    );
  }, [categorias, search]);

  const totalPages = useMemo(() => {
    const tp = Math.ceil(filtered.length / pageSize) || 1;
    return tp;
  }, [filtered.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const nextPage = () => page < totalPages && setPage((p) => p + 1);
  const prevPage = () => page > 1 && setPage((p) => p - 1);

  const openCreateModal = () => {
    setSelectedCategoria(null);
    setShowModal(true);
  };

  const openEditModal = (cat) => {
    setSelectedCategoria(cat);
    setShowModal(true);
  };

  const handleCreate = async (formData) => {
    const res = await createCategoria(formData);
    await loadCategorias();
    setShowModal(false);
    showAlert("success", res?.message || "Category created successfully.");
  };

  const handleUpdate = async (id, formData) => {
    const res = await updateCategoria(id, formData);
    await loadCategorias();
    setShowModal(false);
    showAlert("success", res?.message || "Category updated successfully.");
  };

  return (
    <div className="page-container">
      <style>{modernStyles}</style>

      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="header">
        <h2 className="title">Categories Management</h2>

        <div className="top-bar">
          <input
            type="text"
            placeholder="Search category..."
            className="search-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

          {hasPerm("Create categories") && (
            <button className="btn-create" onClick={openCreateModal}>
              Create Category
            </button>
          )}
        </div>
      </div>

      <div className="table-card">
        <div className="table-scroll">
          <table className="modern-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Category Name</th>
                <th>Status</th>
                <th>Date Created</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    No categories found
                  </td>
                </tr>
              ) : (
                paginated.map((c) => (
                  <tr key={c.categoria_producto_id}>
                    <td>{c.categoria_producto_id}</td>
                    <td>{c.nombre}</td>

                    <td>
                      <span
                        className={
                          Number(c.estado) === 1
                            ? "status active"
                            : "status inactive"
                        }
                      >
                        {Number(c.estado) === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td>{c.fecha_creado?.slice(0, 10)}</td>

                    <td style={{ textAlign: "right" }}>
                      {hasPerm("Update categories") && (
                        <span
                          className="action-icon edit"
                          onClick={() => openEditModal(c)}
                        >
                          {IconEdit}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination">
        {/* Hueco del mismo peso que el total de la derecha, para que el
            grupo del centro quede centrado de verdad. */}
        <span className="pag-lado" aria-hidden="true" />

        <div className="pag-nav">
          <button onClick={prevPage} disabled={page === 1}>
            Prev
          </button>

          <span>
            Page {page} of {totalPages}
          </span>

          <button onClick={nextPage} disabled={page === totalPages}>
            Next
          </button>

          <div className="pag-tamano">
            <label htmlFor="pag-tamano">Rows</label>
            <select
              id="pag-tamano"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={12}>12</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Cuantas filas hay detras del buscador. Con el tope de pagina
            puesto, "12 filas" no dice si hay 12 o 300. */}
        <span className="pag-total">
          {filtered.length}
          {filtered.length === 1 ? " category" : " categories"}
        </span>
      </div>

      {showModal && (
        <CategoryFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          categoriaData={selectedCategoria}
          onSubmit={async (formData) => {
            if (selectedCategoria) {
              await handleUpdate(
                selectedCategoria.categoria_producto_id,
                formData
              );
            } else {
              await handleCreate(formData);
            }
          }}
        />
      )}
    </div>
  );
}

export default CategoriesPage;

const modernStyles = `
.page-container{
  padding: 10px 0;
  font-family: 'Segoe UI';
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* ✅ ALERT */
.omx-alert{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:12px 14px;
  border-radius:12px;
  border:1px solid;
  margin: 0 0 14px 0;
  box-shadow: 0 10px 25px rgba(0,0,0,.08);
}
.omx-alert__msg{ font-size:14px; font-weight:600; }
.omx-alert__x{
  border:none;
  background:transparent;
  cursor:pointer;
  font-size:18px;
  opacity:.7;
}
.omx-alert__x:hover{ opacity:1; }

.omx-alert--success{ background:#ECFDF5; border-color:#A7F3D0; color:#065F46; }
.omx-alert--error{ background:#FEF2F2; border-color:#FECACA; color:#7F1D1D; }
.omx-alert--info{ background:#EFF6FF; border-color:#BFDBFE; color:#1E3A8A; }

.header{
  display:flex;
  flex-direction:column;
  margin-bottom:20px;
}

.title{
  font-size:26px;
  font-weight: 800;
  color:#2F4779;
  margin-bottom:6px;
}

.top-bar{
  display:flex;
  justify-content:space-between;
  margin-top:12px;
  gap:12px;
}

.search-input{
  padding:10px 16px;
  width:260px;
  border-radius:8px;
  border:1px solid #CBD5E1;
  background:#F8FAFC;
  color:#1E293B;
  font-size:14px;
}
.search-input:focus{
  outline:none;
  border-color:#3B82F6;
  background:white;
}

.btn-create{
  padding:10px 18px;
  background:#2F4779;
  color:white;
  border:1px solid #2F4779;
  border-radius:8px;
  font-weight:600;
  cursor:pointer;
  transition:0.25s;
}
.btn-create:hover{ background:#6D7DFF; border-color:#6D7DFF; }

.table-card{
  background:white;
  border:1px solid #E2E8F0;
  border-radius:12px;
  overflow:hidden;
  box-shadow:0 4px 20px rgba(0,0,0,0.05);
  flex: 0 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-scroll{
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  /* ✅ Firefox (igual Orders) */
  scrollbar-width: thin;
  scrollbar-color: #3B82F6 #F1F5F9;
}

/* ✅ Chrome/Edge/Safari (igual Orders) */
.table-scroll::-webkit-scrollbar{
  width: 10px;
  height: 10px;
}

.table-scroll::-webkit-scrollbar-track{
  background: #F1F5F9;
  border-radius: 10px;
}

.table-scroll::-webkit-scrollbar-thumb{
  background: linear-gradient(180deg, #93C5FD, #3B82F6);
  border-radius: 10px;
  border: 2px solid #F1F5F9;
}

.table-scroll::-webkit-scrollbar-thumb:hover{
  background: linear-gradient(180deg, #60A5FA, #2563EB);
}

.modern-table{
  width:100%;
  border-collapse:collapse;
  min-width: 780px;
}

.modern-table th{
  background:#F1F5F9;
  padding:14px;
  font-size:13px;
  color:#475569;
  text-align:left;
  border-bottom:1px solid #E2E8F0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.modern-table td{
  padding:14px;
  font-size:14px;
  color:#334155;
  border-bottom:1px solid #F1F5F9;
}

.modern-table tr:hover td{ background:#F8FAFC; }

.status{
  padding:6px 12px;
  border-radius:20px;
  font-size:12px;
  font-weight:600;
}
.status.active{ background:#DCFCE7; color:#15803D; }
.status.inactive{ background:#FEE2E2; color:#B91C1C; }

.action-icon{
  padding:6px;
  border-radius:6px;
  cursor:pointer;
  transition:.2s;
}
.action-icon.edit:hover{ background:#DBEAFE; }

.no-data{
  text-align:center;
  padding:20px;
  color:#94A3B8;
}

.pagination{
  margin-top: 14px;
  margin-bottom: 2px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
}

/* Los dos lados pesan igual para que el centro quede centrado. */
.pag-lado,
.pag-total{
  flex: 1 1 0;
  min-width: 0;
  font-size: 13px;
  color: #94A3B8;
}

.pag-total{ text-align: right; }

.pag-nav{
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
}

.pag-tamano{
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
}

.pag-tamano label{
  font-size: 12px;
  color: #94A3B8;
}

.pag-tamano select{
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  font-size: 12.5px;
  font-family: inherit;
  cursor: pointer;
}

.pagination button{
  padding: 8px 14px;
  background: #2F4779;
  color: #fff;
  border: 1px solid #2F4779;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.pagination button:hover:not(:disabled){
  background: #6D7DFF;
  border-color: #6D7DFF;
}

.pagination button:disabled{
  opacity: .5;
  cursor: not-allowed;
}

.pagination span{
  font-size: 14px;
  color: #475569;
}

@media (max-width: 720px){
  .top-bar{
    flex-direction: column;
    align-items: stretch;
  }
  .search-input{
    width: 100%;
  }
  .btn-create{
    width: 100%;
  }
  .modern-table{
    min-width: 720px;
  }
}
`;

